/**
 * Employment Types Service
 *
 * Fetches and manages employment types from the database.
 * Employment types are country-specific and determine payroll rules.
 */

import { supabase } from '@/integrations/supabase/client';
import type { EmploymentType, EmploymentTypeOption } from './types';

// =============================================================================
// Fetch Employment Types
// =============================================================================

/**
 * Get all active employment types for a country
 */
export async function getEmploymentTypesByCountry(
  countryCode: string
): Promise<EmploymentType[]> {
  const { data, error } = await supabase
    .from('employment_types')
    .select('*')
    .eq('country_code', countryCode.toUpperCase())
    .eq('is_active', true)
    .order('sort_order');

  if (error) {
    console.error('Error fetching employment types:', error);
    throw new Error(`Failed to fetch employment types: ${error.message}`);
  }

  return (data || []) as EmploymentType[];
}

/**
 * Get employment types for a specific business (uses business country)
 */
export async function getEmploymentTypesForBusiness(
  businessId: string
): Promise<EmploymentType[]> {
  // First get the business country
  const { data: business, error: businessError } = await supabase
    .from('businesses')
    .select('country_code')
    .eq('id', businessId)
    .single();

  if (businessError || !business) {
    console.error('Error fetching business:', businessError);
    throw new Error('Failed to fetch business country');
  }

  const countryCode = business.country_code || 'SK'; // Default to Slovakia
  return getEmploymentTypesByCountry(countryCode);
}

/**
 * Get a single employment type by ID
 */
export async function getEmploymentTypeById(
  id: string
): Promise<EmploymentType | null> {
  const { data, error } = await supabase
    .from('employment_types')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Not found
    }
    console.error('Error fetching employment type:', error);
    throw new Error(`Failed to fetch employment type: ${error.message}`);
  }

  return data as EmploymentType;
}

/**
 * Get employment type by country and code
 */
export async function getEmploymentTypeByCode(
  countryCode: string,
  code: string
): Promise<EmploymentType | null> {
  const { data, error } = await supabase
    .from('employment_types')
    .select('*')
    .eq('country_code', countryCode.toUpperCase())
    .eq('code', code)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Not found
    }
    console.error('Error fetching employment type by code:', error);
    throw new Error(`Failed to fetch employment type: ${error.message}`);
  }

  return data as EmploymentType;
}

// =============================================================================
// Transform for UI
// =============================================================================

/**
 * Transform employment types to UI-friendly options
 */
export function toEmploymentTypeOptions(
  types: EmploymentType[],
  locale: 'native' | 'en' = 'native'
): EmploymentTypeOption[] {
  return types.map((type) => ({
    id: type.id,
    code: type.code,
    label: locale === 'native' ? type.label_native : type.label_en,
    labelEn: type.label_en,
    category: type.category,
    hasLimits: type.max_weekly_hours !== null || type.max_yearly_hours !== null,
  }));
}

/**
 * Get display label for an employment type
 */
export function getEmploymentTypeLabel(
  type: EmploymentType | null | undefined,
  locale: 'native' | 'en' = 'native'
): string {
  if (!type) return '';
  return locale === 'native' ? type.label_native : type.label_en;
}

/**
 * Get category display name
 */
export function getCategoryLabel(
  category: EmploymentType['category'],
  locale: 'sk' | 'en' = 'en'
): string {
  const labels = {
    employee: { sk: 'Zamestnanec', en: 'Employee' },
    agreement: { sk: 'Dohoda', en: 'Agreement' },
    contractor: { sk: 'Dodávateľ', en: 'Contractor' },
  };
  return labels[category]?.[locale] || category;
}

// =============================================================================
// Validation Helpers
// =============================================================================

/**
 * Check if employment type has weekly hours limit
 */
export function hasWeeklyHoursLimit(type: EmploymentType): boolean {
  return type.max_weekly_hours !== null;
}

/**
 * Check if employment type has yearly hours limit
 */
export function hasYearlyHoursLimit(type: EmploymentType): boolean {
  return type.max_yearly_hours !== null;
}

/**
 * Validate hours against employment type limits
 */
export function validateHoursAgainstType(
  type: EmploymentType,
  weeklyHours?: number,
  yearlyHours?: number
): { valid: boolean; error?: string } {
  if (type.max_weekly_hours !== null && weeklyHours !== undefined) {
    if (weeklyHours > type.max_weekly_hours) {
      return {
        valid: false,
        error: `Weekly hours (${weeklyHours}) exceed maximum (${type.max_weekly_hours}) for ${type.code}`,
      };
    }
  }

  if (type.max_yearly_hours !== null && yearlyHours !== undefined) {
    if (yearlyHours > type.max_yearly_hours) {
      return {
        valid: false,
        error: `Yearly hours (${yearlyHours}) exceed maximum (${type.max_yearly_hours}) for ${type.code}`,
      };
    }
  }

  return { valid: true };
}

/**
 * Check if employee age is valid for employment type
 */
export function validateAgeForType(
  type: EmploymentType,
  age: number
): { valid: boolean; error?: string } {
  if (type.min_age !== null && age < type.min_age) {
    return {
      valid: false,
      error: `Age (${age}) is below minimum (${type.min_age}) for ${type.code}`,
    };
  }

  if (type.max_age !== null && age > type.max_age) {
    return {
      valid: false,
      error: `Age (${age}) exceeds maximum (${type.max_age}) for ${type.code}`,
    };
  }

  return { valid: true };
}
